/**
 * Phase 131 (Plan 131-05, D-11) — the optional, read-only "Generate draft" button of one repository
 * row of the Repository policies card.
 *
 * It asks the same endpoint the CLI uses for a first draft of the repository's factory files and
 * shows them for copying. Nothing is saved here and no pull request is opened: a person commits the
 * files in a pull request that another person merges. Generated text comes from a third-party
 * repository, so it is only ever rendered as React text inside a <pre>.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useGenerateOnboardingDraft } from '../../hooks/useProjectEngineering';
import type { OnboardingQuestion } from '../../services/api/projectEngineering';

export interface OnboardingDraftPanelProps {
  projectId: number;
  repository: string;
}

const FACTORY_YML_PATH = '.monkfactory/factory.yml';
const DOCKERFILE_PATH = '.monkfactory/Dockerfile';
const DOCKERIGNORE_PATH = '.monkfactory/Dockerfile.dockerignore';

function CopyButton({ text, label }: { text: string; label: string }): ReactElement {
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard.writeText(text).then(() => setCopied(true));
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy ${label}`}
      className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function FileSection({ path, text }: { path: string; text: string }): ReactElement {
  return (
    <section className="mt-3" data-testid={`onboarding-draft-file-${path}`}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-mono text-xs font-semibold text-gray-900">{path}</h4>
        <CopyButton text={text} label={path} />
      </div>
      <pre className="mt-1 max-h-72 overflow-auto rounded bg-gray-50 p-2 text-xs text-gray-800 whitespace-pre-wrap break-words">
        {text}
      </pre>
    </section>
  );
}

function QuestionItem({ q }: { q: OnboardingQuestion }): ReactElement {
  return (
    <li className="py-1.5" data-testid={`onboarding-draft-question-${q.id}`}>
      <p className="text-xs font-medium text-gray-900">{q.question}</p>
      {q.candidates.length > 0 && (
        <p className="text-xs text-gray-600">{`Choices: ${q.candidates.join(', ')}`}</p>
      )}
      <p className="text-xs text-gray-600">
        {`Default: ${q.default.length > 0 ? q.default.join(', ') : 'none'}`}
      </p>
    </li>
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'The draft could not be generated.';
}

export function OnboardingDraftPanel({
  projectId,
  repository,
}: OnboardingDraftPanelProps): ReactElement {
  const draftM = useGenerateOnboardingDraft(projectId);
  const draft = draftM.data;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => draftM.mutate(repository)}
        disabled={draftM.isPending}
        data-testid={`onboarding-draft-button-${repository}`}
        className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        {draftM.isPending ? 'Generating…' : 'Generate draft'}
      </button>

      {draftM.isError && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {errorMessage(draftM.error)}
        </p>
      )}

      {draft && (
        <div
          className="mt-2 rounded border border-gray-200 p-3"
          data-testid={`onboarding-draft-panel-${repository}`}
        >
          <p className="text-xs text-gray-600">
            A draft to commit in a pull request that a person merges. Nothing is saved here.
          </p>
          <FileSection path={FACTORY_YML_PATH} text={draft.factoryYml} />
          {draft.dockerfile !== null ? (
            <FileSection path={DOCKERFILE_PATH} text={draft.dockerfile} />
          ) : (
            <p className="mt-3 text-xs text-amber-700" data-testid="onboarding-draft-no-runner">
              The runner files were not generated; see the open questions below.
            </p>
          )}
          {draft.dockerignore !== null && (
            <FileSection path={DOCKERIGNORE_PATH} text={draft.dockerignore} />
          )}
          {draft.questions.length > 0 && (
            <section className="mt-3">
              <h4 className="text-xs font-semibold text-gray-900">Open questions</h4>
              <ul
                className="mt-1 divide-y divide-gray-100"
                data-testid="onboarding-draft-questions"
              >
                {draft.questions.map((q) => (
                  <QuestionItem key={q.id} q={q} />
                ))}
              </ul>
            </section>
          )}
          {draft.notes.length > 0 && (
            <section className="mt-3">
              <h4 className="text-xs font-semibold text-gray-900">Notes</h4>
              <ul
                className="mt-1 list-disc pl-4 text-xs text-gray-700"
                data-testid="onboarding-draft-notes"
              >
                {draft.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

export default OnboardingDraftPanel;
